import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkle, MagnifyingGlass, ArrowRight, Crown, CheckCircle, FilePdf,
  MapPin, PaintBrush, Lightning, Detective, ShieldCheck, GoogleLogo, Lock, EnvelopeSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";
import LinkedInInsight from "../components/LinkedInInsight";
import { track } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import LpgDeck from "../components/LpgDeck";
import { SAMPLE_DECK } from "./lpg-demo-sample";

const HERO_IMG = "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBtYW5zaW9uJTIwZHVza3xlbnwwfHx8fDE3OTAwMDIxNDV8MA&ixlib=rb-4.1.0&q=85";
const SHOWCASE_IMG = "https://images.pexels.com/photos/38975400/pexels-photo-38975400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const MARBLE_IMG = "https://images.unsplash.com/photo-1606767041004-6b918abe92be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtYXJibGUlMjB0ZXh0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzkwMDAyMTQ1fDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];
const fadeUp = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.8, ease: EASE } };

const SCHEMES = [
  { name: "Reelty", primary: "#4A0000", accent: "#B08D57" },
  { name: "Navy", primary: "#0C2340", accent: "#B08D57" },
  { name: "Forest", primary: "#1F3D2B", accent: "#C9A96A" },
  { name: "Charcoal", primary: "#1A1A1A", accent: "#C9A96A" },
  { name: "Royal", primary: "#012169", accent: "#B7975B" },
];

const PERKS = [
  { icon: FilePdf, title: "Unlimited branded presentations", text: "Fully editable, with your photo, logo & colors." },
  { icon: MapPin, title: "LeadLander landing pages", text: "A shareable page for every listing." },
  { icon: Sparkle, title: "ListAssist AI copy", text: "MLS descriptions & social captions." },
  { icon: Crown, title: "360° tours + drone", text: "Included on every All-Inclusive shoot." },
  { icon: Lightning, title: "Priority scheduling", text: "Front-of-line when media can't wait." },
  { icon: ShieldCheck, title: "Locked partner pricing", text: "Member rates + 25% off every shoot." },
];

const STAGES = [
  "Pulling public records…",
  "Curating comparable sales…",
  "Reading the local market…",
  "Structuring your pricing strategy…",
  "Composing the editorial layout…",
];

const STEPS = [
  { n: "01", t: "Enter an address", d: "Type any property. We pull the records, comparable sales and live local market in seconds." },
  { n: "02", t: "We build the strategy", d: "A defensible pricing range, comp analysis, neighborhood narrative and a clear recommendation." },
  { n: "03", t: "Present & win", d: "Review it live, recolor it to any brand, and download a client-ready PDF on the spot." },
];

const Overline = ({ children, dark }) => (
  <span className={`inline-flex items-center gap-3 text-[11px] font-black tracking-[0.28em] uppercase ${dark ? "text-[#E4C97E]" : "text-[#B08D57]"}`}>
    <span className={`h-px w-8 ${dark ? "bg-[#B08D57]" : "bg-[#B08D57]"}`} />{children}
  </span>
);

export default function LpgDemo() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [results, setResults] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [eta, setEta] = useState(55);
  const [stageIdx, setStageIdx] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [scheme, setScheme] = useState(0);
  const [sample, setSample] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [suEmail, setSuEmail] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suName, setSuName] = useState("");
  const [suBusy, setSuBusy] = useState(false);
  const acTimer = useRef(null);
  const acSeq = useRef(0);
  const progTimer = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);

  // Make the two headline lines exactly equal width on desktop (left-aligned, no gaps).
  useLayoutEffect(() => {
    const fit = () => {
      const a = line1Ref.current, b = line2Ref.current;
      if (!a || !b) return;
      a.style.transform = ""; b.style.transform = "";
      if (window.innerWidth < 1024) return;
      const wa = a.offsetWidth, wb = b.offsetWidth;
      if (!wa || !wb) return;
      const target = Math.max(wa, wb);
      a.style.transform = `scaleX(${(target / wa).toFixed(4)})`;
      b.style.transform = `scaleX(${(target / wb).toFixed(4)})`;
    };
    fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  useEffect(() => () => { clearInterval(progTimer.current); clearTimeout(acTimer.current); }, []);

  // Deep-link from the trial-invite email: /lpg-demo?trial=1&email=... auto-opens signup.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("trial") === "1") {
      const em = (p.get("email") || "").trim();
      if (em) { setEmail(em); setSuEmail(em); }
      setSignupOpen(true);
    }
  }, []);

  const onType = (v) => {
    setQ(v); setSel(null);
    clearTimeout(acTimer.current);
    if (v.trim().length < 3) { setResults([]); return; }
    const mine = ++acSeq.current;
    acTimer.current = setTimeout(async () => {
      try { const r = await client.get("/booking/autocomplete", { params: { q: v } }); if (mine === acSeq.current) setResults(r.data.results || []); }
      catch { if (mine === acSeq.current) setResults([]); }
    }, 160);
  };

  const pick = async (r) => {
    setResults([]);
    setQ(r.label);
    setResolving(true);
    try {
      const { data } = await client.post("/booking/resolve-address",
        { street: r.street, city: r.city, state: r.state, zip: r.zip, lat: r.lat, lon: r.lon });
      setSel(data);
      setQ(data.label || r.label);
      if (!data.complete) toast.error("We couldn't confirm a full address (with ZIP) for that pick — please choose another suggestion.");
    } catch {
      setSel(null);
      toast.error("Couldn't verify that address. Please pick another from the list.");
    }
    setResolving(false);
  };

  const pollJob = (jobId) => new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await client.get(`/lpg/demo/status/${jobId}`);
        if (data.status === "done") return resolve(data.draft);
        if (data.status === "error") return reject(new Error(data.detail || "Couldn't generate. Try again."));
      } catch { /* transient — keep polling */ }
      if (Date.now() - started > 180000) return reject(new Error("Generation timed out. Please try again."));
      setTimeout(tick, 2500);
    };
    tick();
  });

  const generate = async () => {
    if (!sel || !sel.complete) { toast.error("Please select a complete address (with ZIP) from the dropdown."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Enter a valid email — we'll send your download link there."); return; }
    setBusy(true); setProgress(4); setEta(55); setStageIdx(0);
    const start = Date.now();
    clearInterval(progTimer.current);
    progTimer.current = setInterval(() => {
      const el = (Date.now() - start) / 1000;
      setProgress(Math.min(96, (el / 55) * 100));
      setEta(Math.max(1, Math.ceil(55 - el)));
      setStageIdx(Math.min(STAGES.length - 1, Math.floor(el / 11)));
    }, 400);
    try {
      const payload = { email, address: sel.street, city: sel.city, state: sel.state, zip: sel.zip, lat: sel.lat, lon: sel.lon };
      const { data: job } = await client.post("/lpg/demo/generate", payload);
      if (job.limit_reached) {
        toast.error(job.message || "You've used your free presentation.");
        setBusy(false); clearInterval(progTimer.current); setProgress(0);
        document.getElementById("upsell")?.scrollIntoView({ behavior: "smooth" });
        return;
      }
      const draft = await pollJob(job.job_id);
      setProgress(100); setScheme(0); setViewing({ data: draft });
      track("generate_lead", { content_category: "lpg_demo" });
    } catch (e) {
      toast.error(e?.message || "Couldn't generate. Please try again.");
    }
    clearInterval(progTimer.current);
    setBusy(false);
    setTimeout(() => setProgress(0), 700);
  };

  const goSignup = () => { setSuEmail(email || ""); setSignupOpen(true); };

  const registerEmail = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(suEmail.trim())) { toast.error("Enter a valid email."); return; }
    if ((suPassword || "").length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setSuBusy(true);
    try {
      const { data } = await client.post("/auth/register", { email: suEmail.trim(), password: suPassword, name: suName.trim() });
      login(data.token, data.user);
      track("sign_up", { method: "trial" });
      track("start_trial");
      toast.success("Welcome to Reelty — your 30-day Penthouse trial is live.");
      nav("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create your account."); }
    setSuBusy(false);
  };

  const googleSignup = () => {
    track("sign_up", { method: "google" });
    sessionStorage.setItem("reelty_signup", "1");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
  };

  const inputCls = "h-12 bg-white/[0.04] border-0 border-b border-[#B08D57]/35 rounded-none text-[#FBFAF7] placeholder:text-white/35 focus-visible:ring-0 focus-visible:border-[#B08D57] transition-colors";

  const brandBar = (
    <div className="hidden md:flex items-center gap-3 mr-1">
      <div className="flex items-center gap-1.5" data-testid="lpg-demo-scheme-swatches">
        {SCHEMES.map((s, i) => (
          <button key={s.name} title={s.name} onClick={() => setScheme(i)} data-testid={`lpg-demo-scheme-${i}`}
            className={`w-6 h-6 rounded-full border transition-transform hover:scale-110 ${scheme === i ? "ring-2 ring-offset-1" : "border-black/15"}`}
            style={{ background: s.primary, boxShadow: `inset 0 0 0 2px ${s.accent}`, ["--tw-ring-color"]: s.accent }} />
        ))}
      </div>
      <button onClick={goSignup} data-testid="lpg-deck-trial-cta"
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-[5px] bg-[#B08D57] text-[#1A1715] hover:bg-[#c29a5f] whitespace-nowrap">
        <Crown size={14} weight="fill" />Unlimited branded — free trial
      </button>
    </div>
  );

  const FormCard = (
    <div className="relative w-full max-w-md rounded-[6px] bg-black/45 backdrop-blur-2xl border border-[#B08D57]/25 shadow-[0_30px_80px_rgba(0,0,0,0.55)] p-6 sm:p-8"
      data-testid="lpg-demo-form-card">
      {!busy ? (
        <>
          <p className="text-[11px] font-black tracking-[0.28em] uppercase text-[#E4C97E]">Free · No account</p>
          <h3 className="font-display text-2xl text-[#FBFAF7] mt-2 leading-snug">Generate your first presentation</h3>
          <div className="relative mt-6">
            <MagnifyingGlass size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#B08D57]" />
            <Input value={q} onChange={(e) => onType(e.target.value)} data-testid="lpg-demo-address"
              placeholder="Property address" className={`${inputCls} pl-7`} />
            {results.length > 0 && !sel && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-[#1A1715]/95 backdrop-blur-xl border border-[#B08D57]/25 rounded-[5px] shadow-xl max-h-56 overflow-y-auto" data-testid="lpg-demo-results">
                {results.map((r, i) => (
                  <button key={i} onClick={() => pick(r)} data-testid={`lpg-demo-opt-${i}`}
                    className="block w-full text-left px-3 py-2.5 text-sm text-white/85 hover:bg-white/5">{r.label}</button>
                ))}
              </div>
            )}
          </div>
          <div className="relative mt-4">
            <EnvelopeSimple size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#B08D57]" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="lpg-demo-email"
              placeholder="Email — where your download link goes" className={`${inputCls} pl-7`} />
          </div>
          <Button onClick={generate} data-testid="generate-lpg-button" disabled={busy || resolving || !sel?.complete}
            className="group w-full mt-7 h-12 bg-[#4A0000] hover:bg-[#5C1A1A] text-[#FBFAF7] font-semibold rounded-[5px] tracking-wide hover:tracking-wider transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:tracking-wide">
            <Sparkle size={17} weight="fill" className="mr-2 text-[#E4C97E]" />{resolving ? "Verifying address…" : "Generate free presentation"}
            <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
          <p className="text-[12px] text-white/45 mt-4 flex items-center gap-1.5" data-testid="lpg-demo-hint">
            <CheckCircle size={14} weight="fill" className="text-[#B08D57]" />
            {sel?.complete ? "Ready in about a minute · view & download instantly" : "Start typing and pick your address from the list"}
          </p>
        </>
      ) : (
        <div className="py-4" data-testid="lpg-demo-progress">
          <p className="text-[11px] font-black tracking-[0.28em] uppercase text-[#E4C97E]">Composing</p>
          <motion.h3 key={stageIdx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="font-display text-2xl text-[#FBFAF7] mt-3 leading-snug">{STAGES[stageIdx]}</motion.h3>
          <div className="mt-8 h-px w-full bg-white/10 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#B08D57] to-[#E4C97E] transition-[width] duration-500 ease-out"
              style={{ width: `${progress}%` }} data-testid="lpg-demo-progress-bar" />
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-white/40">
            <span className="tabular-nums">{Math.round(progress)}%</span>
            <span className="tabular-nums">~{eta}s remaining</span>
          </div>
          <p className="text-[12px] text-white/45 mt-6 leading-relaxed">We're grounding every figure in this property's records, comps and local market — the way a top listing agent would.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-[#14110F] text-[#FBFAF7] antialiased">
      <LinkedInInsight />
      {/* Nav */}
      <header className="absolute top-0 inset-x-0 z-40 flex items-center justify-between px-7 sm:px-14 py-6">
        <img src="/reelty-r.png" alt="Reelty" className="h-14 w-auto object-contain -ml-[18px] drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]" data-testid="lpg-demo-logo" />
        <button onClick={() => nav("/login")} data-testid="lpg-demo-signin"
          className="text-sm text-white/70 hover:text-[#E4C97E] transition-colors duration-300">Sign in</button>
      </header>

      {/* HERO — asymmetric editorial */}
      <section className="min-h-screen p-3 sm:p-5 flex">
        <div className="relative flex-1 rounded-[6px] border border-[#B08D57]/25 overflow-hidden grid lg:grid-cols-[1.05fr_0.95fr] grain">
          {/* Left: typography */}
          <div className="order-2 lg:order-1 relative z-10 flex flex-col justify-center px-7 sm:px-14 py-16 lg:py-14">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
              <h1 className="font-display font-bold leading-[0.94] tracking-tight text-[#FBFAF7] flex flex-col items-start uppercase">
                <span ref={line1Ref} className="origin-left text-3xl sm:text-4xl lg:text-6xl">Win the listing</span>
                <span ref={line2Ref} className="origin-left text-base sm:text-xl lg:text-3xl mt-1.5 lg:mt-3 lg:whitespace-nowrap text-[#E4C97E]"><span className="italic">before</span> you walk in the door.</span>
              </h1>
              <p className="text-base sm:text-lg text-white/65 mt-7 max-w-md leading-relaxed">
                Enter any address and Reelty composes a complete, data-backed pricing &amp; market presentation — comps, strategy, neighborhood, the whole story — in about a minute.
              </p>
              <div className="flex items-center gap-8 mt-10">
                {[["~1 min", "to generate"], ["11", "editorial slides"], ["$0", "no account"]].map(([a, b]) => (
                  <div key={b}>
                    <p className="font-display text-3xl text-[#E4C97E]">{a}</p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 mt-1">{b}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => setSample(true)} data-testid="lpg-demo-see-example"
                className="mt-10 inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#E4C97E] transition-colors duration-300 group">
                <FilePdf size={18} weight="duotone" className="text-[#B08D57]" />See a full example presentation
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </button>
            </motion.div>
          </div>

          {/* Right: cinematic image + floating glass form */}
          <div className="order-1 lg:order-2 relative min-h-[64vh] lg:min-h-full flex items-start lg:items-center justify-center p-5 sm:p-10 pt-24 lg:pt-10">
            <div className="absolute inset-0">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/45" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14110F] via-transparent to-transparent lg:bg-gradient-to-r lg:from-[#14110F]" />
            </div>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }} className="relative z-10 w-full flex justify-center">
              {FormCard}
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — staggered editorial on cream */}
      <section className="bg-[#FBFAF7] text-[#14110F] px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}><Overline>How it works</Overline>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4 max-w-2xl leading-[1.05]">A listing-winning presentation, in three moves.</h2>
          </motion.div>
          <div className="mt-16 space-y-px">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
                className={`grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-12 items-baseline py-10 border-t border-[#14110F]/10 ${i % 2 ? "sm:pl-24" : ""}`}>
                <span className="font-display text-6xl sm:text-7xl text-[#4A0000]/15 leading-none">{s.n}</span>
                <div className="max-w-xl">
                  <h3 className="font-display text-2xl sm:text-3xl text-[#14110F]">{s.t}</h3>
                  <p className="text-[#5C5A58] mt-3 leading-relaxed text-[15px]">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE — bento on obsidian */}
      <section className="bg-[#14110F] px-6 sm:px-12 py-24 sm:py-32 grain">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl"><Overline dark>See what you'll get</Overline>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4 leading-[1.05] text-[#FBFAF7]">Eleven slides that make you the market expert in the room.</h2>
          </motion.div>
          <div className="mt-14 grid lg:grid-cols-12 gap-4">
            <motion.div {...fadeUp} className="lg:col-span-8 relative rounded-[6px] overflow-hidden border border-[#B08D57]/25 group cursor-pointer min-h-[340px]"
              onClick={() => setSample(true)} data-testid="lpg-demo-showcase-tile">
              <img src={SHOWCASE_IMG} alt="Sample listing presentation" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14110F] via-[#14110F]/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8">
                <p className="text-[11px] font-black tracking-[0.25em] uppercase text-[#E4C97E]">18 Coral Reef Court · Naples, FL</p>
                <p className="font-display text-2xl sm:text-3xl text-[#FBFAF7] mt-2">A complete pricing presentation</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-white/80 group-hover:text-[#E4C97E] transition-colors">
                  <FilePdf size={17} weight="fill" />Open the sample deck<ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }} className="lg:col-span-4 rounded-[6px] border border-[#B08D57]/25 p-8 flex flex-col justify-between bg-white/[0.02]">
              <div>
                <PaintBrush size={30} weight="duotone" className="text-[#B08D57]" />
                <h3 className="font-display text-2xl text-[#FBFAF7] mt-4">Recolor it to any brand.</h3>
                <p className="text-white/55 text-sm mt-3 leading-relaxed">Preview the deck in any brokerage's colors instantly. As a Penthouse Partner, every presentation is fully branded and editable — your photo, logo and voice.</p>
              </div>
              <Button onClick={() => setSample(true)} data-testid="lpg-demo-view-sample"
                className="mt-8 h-12 bg-[#B08D57] hover:bg-[#c29a5f] text-[#1A1715] font-semibold rounded-[5px]">
                <FilePdf size={17} weight="fill" className="mr-2" />View the sample deck
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* UPSELL — membership card */}
      <section id="upsell" className="relative bg-[#FBF6EA] text-[#14110F] px-6 sm:px-12 py-24 sm:py-32 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <Overline>The membership</Overline>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4 leading-[1.05]">Go unlimited as a <span className="italic font-light text-[#4A0000]">Penthouse Partner.</span></h2>
            <p className="text-[#5C5A58] mt-5 leading-relaxed">Everything you need to win listings and market them beautifully — free for 30 days, no card required to start.</p>
          </motion.div>

          <div className="mt-16 grid lg:grid-cols-[1fr_0.9fr] gap-10 items-stretch">
            {/* Perks */}
            <motion.div {...fadeUp} className="grid sm:grid-cols-2 gap-x-8 gap-y-7 self-center">
              {PERKS.map((p, i) => (
                <div key={i} className="flex gap-3.5">
                  <span className="shrink-0 w-10 h-10 rounded-[5px] bg-[#4A0000]/8 border border-[#4A0000]/10 flex items-center justify-center">
                    <p.icon size={18} weight="fill" className="text-[#4A0000]" />
                  </span>
                  <div>
                    <p className="font-semibold text-[15px] text-[#14110F] leading-snug">{p.title}</p>
                    <p className="text-[13px] text-[#5C5A58] leading-snug mt-1">{p.text}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Membership card */}
            <motion.div {...fadeUp} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
              className="relative rounded-[8px] overflow-hidden text-[#FBFAF7] p-9 border border-[#B08D57]/30 shadow-[0_30px_80px_rgba(74,0,0,0.28)]"
              style={{ background: "linear-gradient(155deg,#4A0000 0%,#5C1A1A 52%,#2A0A0A 100%)" }}>
              <img src={MARBLE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.08] mix-blend-screen pointer-events-none" />
              <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-[#B08D57]/25 blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.2em] uppercase text-[#E4C97E]"><Crown size={15} weight="fill" />Penthouse Partner</span>
                  <span className="text-[10px] font-black tracking-[0.18em] uppercase text-[#1A1715] bg-[#E4C97E] rounded-full px-2.5 py-1">Launch offer</span>
                </div>
                <div className="mt-7 flex items-end gap-2">
                  <span className="font-display text-6xl leading-none">$45</span>
                  <span className="text-white/55 mb-2">/month</span>
                </div>
                <p className="text-[#E4C97E] text-sm mt-3 leading-relaxed">First month free · then your entire first year for just <b>$45</b> · renews at $450/yr. Or stay monthly at $45.</p>
                <div className="my-6 h-px bg-white/15" />
                <ul className="space-y-2.5">
                  {["30-day free trial — no card to start", "Unlimited branded, editable presentations", "LeadLander + ListAssist included", "360° tours & drone on every All-Inclusive shoot", "Cancel anytime"].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-white/85"><CheckCircle size={18} weight="fill" className="text-[#E4C97E] shrink-0 mt-0.5" />{t}</li>
                  ))}
                </ul>
                <Button onClick={goSignup} data-testid="lpg-demo-start-trial"
                  className="group w-full mt-8 h-13 py-3.5 bg-[#E4C97E] hover:bg-[#efd792] text-[#1A1715] font-bold text-base rounded-[5px] tracking-wide hover:tracking-wider transition-all duration-300">
                  <Crown size={18} weight="fill" className="mr-2" />Start my free 30-day trial
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <p className="text-center text-white/45 text-xs mt-3">No charge today · cancel anytime</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-[#14110F] px-6 sm:px-12 py-28 grain overflow-hidden">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div {...fadeUp}>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#FBFAF7]">Try it on your next listing.</h2>
            <p className="text-white/60 mt-5 max-w-xl mx-auto">Free, no account, ready in about a minute. See exactly why agents win more listings with Reelty.</p>
            <Button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="lpg-demo-final-cta"
              className="group mt-9 h-13 px-9 py-3.5 bg-[#4A0000] hover:bg-[#5C1A1A] text-[#FBFAF7] font-semibold rounded-[5px] tracking-wide hover:tracking-wider transition-all duration-300">
              <Sparkle size={17} weight="fill" className="mr-2 text-[#E4C97E]" />Generate my free presentation
            </Button>
          </motion.div>
        </div>
        <div className="max-w-6xl mx-auto mt-20 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 relative z-10">
          <span className="font-display text-xl text-[#FBFAF7]">Reelty</span>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-white/50 hover:text-[#E4C97E] text-xs transition-colors" data-testid="lpg-demo-privacy-link">Privacy &amp; Cookies</a>
            <span className="text-white/40 text-xs">© {new Date().getFullYear()} Reelty · Real Estate Media &amp; Market Strategy</span>
          </div>
        </div>
      </section>

      {/* Result deck */}
      {viewing && (
        <LpgDeck data={viewing.data} branding={{ ...SCHEMES[scheme], theme: "editorial" }} onClose={() => setViewing(null)} toolbarExtra={brandBar} />
      )}
      {/* Sample deck */}
      {sample && (
        <LpgDeck data={SAMPLE_DECK} branding={{ ...SCHEMES[scheme], theme: "editorial" }} onClose={() => setSample(false)} toolbarExtra={brandBar} />
      )}

      {/* Signup modal */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-md bg-[#FBFAF7]" data-testid="lpg-demo-signup-modal">
          <DialogHeader>
            <DialogTitle className="text-left font-display text-2xl text-[#4A0000] flex items-center gap-2">
              <Crown size={22} weight="fill" className="text-[#B08D57]" />Start your free trial
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5A58] -mt-1">
            Create your Penthouse Partner account — <b className="text-[#4A0000]">30 days free, no card required</b>. Unlimited branded, editable presentations with your agent info, plus LeadLander, ListAssist &amp; member pricing.
          </p>
          <button onClick={googleSignup} data-testid="lpg-demo-google-signup"
            className="mt-4 w-full h-11 rounded-[6px] border border-[#E9DDC6] bg-white hover:bg-[#FAF7F0] flex items-center justify-center gap-2 text-sm font-medium text-[#1A1715] transition-colors">
            <GoogleLogo size={18} weight="bold" className="text-[#4A0000]" />Continue with Google
          </button>
          <div className="flex items-center gap-3 my-3">
            <span className="h-px flex-1 bg-[#E9DDC6]" /><span className="text-[11px] text-[#9A968E] uppercase tracking-wide">or</span><span className="h-px flex-1 bg-[#E9DDC6]" />
          </div>
          <div className="space-y-2.5">
            <Input value={suName} onChange={(e) => setSuName(e.target.value)} placeholder="Full name (optional)" className="h-11 border-[#E9DDC6]" data-testid="lpg-demo-su-name" />
            <div className="relative">
              <EnvelopeSimple size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
              <Input value={suEmail} onChange={(e) => setSuEmail(e.target.value)} type="email" placeholder="Work email" className="h-11 pl-9 border-[#E9DDC6]" data-testid="lpg-demo-su-email" />
            </div>
            <div className="relative">
              <Lock size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
              <Input value={suPassword} onChange={(e) => setSuPassword(e.target.value)} type="password" placeholder="Create a password (8+ characters)" className="h-11 pl-9 border-[#E9DDC6]" data-testid="lpg-demo-su-password" onKeyDown={(e) => e.key === "Enter" && registerEmail()} />
            </div>
            <Button onClick={registerEmail} disabled={suBusy} data-testid="lpg-demo-su-submit"
              className="w-full h-11 bg-[#4A0000] hover:bg-[#5C1A1A] text-white font-semibold">
              {suBusy ? "Creating your account…" : "Start my free trial"}
            </Button>
          </div>
          <p className="text-[12px] text-[#8A8782] mt-1 text-center">After your trial: first year just $45, then $450/yr — or $45/mo. Card only needed to continue.</p>
          <p className="text-[12px] text-[#8A8782] text-center">
            Already have an account? <button onClick={() => nav("/login")} className="text-[#4A0000] underline" data-testid="lpg-demo-su-signin">Sign in</button>
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
